<template>
  <div
    class="contact-search-bar"
    :class="{
      'contact-search-bar--focused': isDropdownOpen,
      'contact-search-bar--readonly': isReadonly,
      'no-hover': noHover,
    }">
    <vue-tel-input
      v-model="prefix"
      :defaultCountry="group.branch || defaultCountry"
      :inputOptions="{showDialCode: true, disabled: isReadonly, readOnly: isReadonly}"
      :preferredCountries="preferredCountries"
      :allCountries="allCountries"
      :dropdownOptions="{
        showSearchBox: true,
        showFlags: false,
        showDialCodeInList: true,
      }"
      @open="handlePrefixOpen"
      @close="
        () => {
          isPrefixSelectOpen = false
        }
      "
      @country-changed="handleCountryChange"
      class="contact-search-bar__select-prefix"
      :disabled="isReadonly">
      <template v-slot:arrow-icon>
        <div class="contact-search-bar__arrow-container">
          <v-icon
            v-if="isReadonly === false"
            class="fas fa-sort-up"
            :class="{'rotated-icon': !isPrefixSelectOpen}"
            size="13" />
          <span>{{ prefix }}</span>
        </div>
      </template>
    </vue-tel-input>
    <input
      v-model="phoneNumber"
      @keypress="handleKeyPress"
      @keyup.enter="handleSearchContact"
      @input="handlePhoneChange"
      @focus="
        () => {
          isDropdownOpen = true
        }
      "
      :readonly="isReadonly"
      data-cy="search-contact-field"
      ref="phoneInput"
      :placeholder="$t('contact.search-bar.placeholder')"
      type="text"
      class="contact-search-bar__telephone-input" />
    <m-btn
      v-if="isSearchable"
      class="contact-search-bar__button contact-search-bar__button--search-bar"
      variant="outlined"
      :loading="isLoading"
      :disabled="!isValidPhone"
      @click="handleSearchContact">
      <div>
        <v-icon class="fas fa-search" size="14" style="margin-bottom: 2px" />
      </div>
    </m-btn>
    <ul v-if="isDropdownOpen && isContactSearched" class="contact-search-bar__options">
      <template v-if="client">
        <li
          :key="client.phone"
          class="contact-search-bar__option"
          :class="{
            'contact-search-bar__option--offer': isOffer,
            'contact-search-bar__option--creating': ['leads', 'client-history'].some(routeName =>
              origin.includes(routeName),
            ),
          }">
          <div :class="`contact-search-bar__option-info${isCreatingLead ? '--creating' : ''}`">
            <template v-if="!isCreatingLead">
              <span class="contact-search-bar__info">
                {{ `${client.firstName || '-'} ${client.lastName || ''}` }}
              </span>
            </template>
            <template v-else>
              <span class="contact-search-bar__info">{{ `${client.firstName || '-'} ${client.lastName || ''}` }}</span>
            </template>
            <span class="contact-search-bar__info contact-search-bar__info--secondary">{{
              client.phone | formatPhone
            }}</span>
            <span class="contact-search-bar__info contact-search-bar__info--secondary">{{ client.email }}</span>
          </div>
          <div class="contact-search-bar__option-buttons">
            <m-btn
              :class="[
                `contact-search-bar__button${isCreatingLead ? '--creating' : ''}`,
                {'contact-search-bar__button--view-history': !isOffer},
              ]"
              variant="outlined"
              @click="onSeeClientHistory"
              v-if="!origin.includes('client-history')"
              data-cy="btn-next">
              <div class="d-flex">
                <v-icon dense class="mr-2">fa-light fa-user</v-icon>
                <span>{{ clientHistoryTextButton }}</span>
              </div>
            </m-btn>
            <m-btn
              class="contact-search-bar__button contact-search-bar__button--primary"
              variant="outlined"
              data-cy="btn-next"
              v-if="isOffer"
              @click="onCreateOfferMotorbike">
              <div>
                <v-icon class="fak fa-light fa-m-file-plus" size="19" />
                <span>{{ actionFoundButtonText }}</span>
              </div>
            </m-btn>
            <m-btn
              :class="`contact-search-bar__button${
                isCreatingLead ? '--creating' : ''
              } contact-search-bar__button--primary`"
              variant="outlined"
              @click="handleContinueCreatingLead"
              v-if="isCreatingLead">
              <div class="d-flex">
                <v-icon dense class="mr-2">fak fa-light fa-m-file-plus</v-icon>
                <span>{{ $t('buttons.continue-creating-lead') }}</span>
              </div>
            </m-btn>
          </div>
        </li>
      </template>
      <template v-if="!client && isContactSearched">
        <li class="contact-search-bar__no-option">
          <v-icon class="fa-light fa-user" color="primary" size="24" />
          <p class="contact-search-bar__no-option-title">
            {{ $t('contact.search-bar.no-client') }}
          </p>
          <p class="contact-search-bar__no-option-subtitle">{{ noClientSubtitle }}</p>
          <div class="contact-search-bar__no-option-buttons" v-if="!isOffer">
            <m-btn
              class="contact-search-bar__button"
              variant="outlined"
              @click="onCreateOfferMotorbike"
              data-cy="btn-next"
              :disabled="isCreateDisabled">
              <div>
                <v-icon class="fak fa-light fa-m-file-plus" size="19" />
                <span>{{ $t('buttons.create-offer') }}</span>
              </div>
            </m-btn>
            <m-btn
              class="contact-search-bar__button contact-search-bar__button--primary"
              variant="outlined"
              @click="openLeadDialog">
              <div>
                <v-icon class="fa-light fa-bullseye-arrow" size="19" />
                <span>{{ $t('buttons.create-lead') }}</span>
              </div>
            </m-btn>
          </div>
          <div class="contact-search-bar__no-option-button" v-if="isOffer">
            <m-btn
              class="contact-search-bar__button contact-search-bar__button--primary"
              variant="outlined"
              @click="onCreateOfferMotorbike"
              data-cy="btn-next">
              <div>
                <v-icon class="fak fa-light fa-m-file-plus" size="19" />
                <span>{{ $t('buttons.create-offer') }}</span>
              </div>
            </m-btn>
          </div>
        </li>
      </template>
    </ul>
  </div>
</template>
<script>
import Inputmask from 'inputmask'
import {getCountryCallingCode, getExampleNumber, parsePhoneNumber} from 'libphonenumber-js'
import examples from 'libphonenumber-js/mobile/examples'
import {VueTelInput} from 'vue-tel-input'
import {mapActions, mapGetters} from 'vuex'

import {DealersContactsApi} from '@/api/dealersContacts.api'
import router from '@/router'
import {getAllCountriesWithPrefixes} from '@/utils/countries'
export default {
  name: 'ContactSearchBar',
  components: {VueTelInput},
  props: {
    noClientSubtitle: String,
    isOffer: Boolean,
    clientHistoryTextButton: String,
    actionFoundButtonText: String,

    noHover: {
      type: Boolean,
      required: false,
      default: false,
    },

    initialPrefix: {
      type: String,
      required: false,
      default: '',
    },
    initialPhoneNumber: {
      type: String,
      required: false,
      default: '',
    },

    isSearchable: {
      type: Boolean,
      required: false,
      default: true,
    },

    isReadonly: {
      type: Boolean,
      required: false,
      default: false,
    },

    origin: {
      type: String,
      required: false,
      default: '',
    },

    isCreatingLead: {
      type: Boolean,
      required: false,
      default: false,
    },

    isTopBar: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      phoneNumber: this.initialPhoneNumber || '',
      searchPrefixQuery: '',
      prefix: this.initialPrefix || '',
      client: null,
      isPrefixSelectOpen: false,
      isDropdownOpen: false,
      isLoading: false,
      placeholder: '',
      prefixCountrySelected: '',
      isValidPhone: false,
      isContactSearched: false,
      preferredCountries: ['ES', 'PT'],
    }
  },

  watch: {
    phoneNumber: {
      handler(newValue) {
        this.isValidPhone = this.validatePhoneNumber(newValue)
        this.$emit('update:is-valid-phone', this.isValidPhone)
        this.$emit('update:phone-number', this.getFormattedPhoneNumber())
      },
      immediate: true,
    },
    initialPhoneNumber: {
      handler(newValue) {
        if (this.isTopBar) {
          this.phoneNumber = newValue
        }
      },
      immediate: true,
    },
  },

  mounted() {
    document.addEventListener('click', this.handleClickOutside)
  },

  computed: {
    ...mapGetters({
      group: 'getGroup',
    }),
    allCountries() {
      return getAllCountriesWithPrefixes(this.group.locale.split('-')[0])
    },
    isCreateDisabled() {
      return this.$route.path === '/offer-manager/create' && this.origin === 'header'
    },
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  },

  methods: {
    ...mapActions(['showAlertSuccess', 'setAlertResult']),

    openLeadDialog() {
      this.isDropdownOpen = false
      this.$emit('open-lead-dialog', this.getFormattedPhoneNumber())
    },

    handleContinueCreatingLead() {
      this.$emit('get-dealer-contact-data', this.client)
      this.isDropdownOpen = false
      this.isContactSearched = false
    },

    async onCreateOfferMotorbike() {
      this.$store.commit('RESET_OFFER')

      if (!this.client) {
        this.client = {
          phone: this.getFormattedPhoneNumber(),
        }
      } else {
        this.client = {...this.client, gdprConsent: true, privacyTermsAccepted: true}
      }

      this.$store.commit('SET_OFFER_SEARCHED_CONTACT', this.client)
      this.$store.commit('SET_FROM_OFFER', this.origin === '/offer-manager/create')
      this.sendGAEvent('InitOfferClientHistory', 'Steps')

      await router.push({name: 'CreateOfferMotorbike'})

      this.resetSearchBar()
    },

    async onSeeClientHistory() {
      const {id} = this.client
      await router.push({name: 'ClientHistory', params: {id}})
      this.resetSearchBar()
    },

    async handleSearchContact() {
      if (!this.isValidPhone) return

      this.isLoading = true
      this.isContactSearched = false

      try {
        const res = await new DealersContactsApi().getContactByPhone(
          this.getFormattedPhoneNumber(),
          this.group.dealerId,
        )
        this.client = res.data.data.length ? res.data.data[0] : null

        if (this.isCreatingLead && !this.client) {
          this.$emit('get-dealer-contact-data', this.client)
          return
        }

        this.isContactSearched = true
        this.isDropdownOpen = true
      } catch (error) {
        this.setAlertResult({
          type: 'error',
          stringId: 'toast.search-contact.error',
          open: true,
        })
      } finally {
        this.isLoading = false
      }
    },

    handleClickOutside(event) {
      const isClickInside = this.$el.contains(event.target)

      if (!isClickInside) {
        this.isDropdownOpen = false
      }
    },

    handlePrefixOpen() {
      this.isPrefixSelectOpen = true
      this.isDropdownOpen = false
      this.$nextTick(() => {
        const searchInput = document.querySelector('.vti__search_box')
        searchInput.style.setProperty('--placeholder-color', '#969696')

        if (searchInput) {
          searchInput.placeholder = this.$t('contact.search-bar.search-country')

          searchInput.addEventListener('input', ({target: {value}}) => {
            searchInput.placeholder = this.$t('contact.search-bar.search-country')

            if (value) {
              this.preferredCountries = []
              return
            }

            searchInput.style.setProperty('--placeholder-color', 'transparent')
            this.preferredCountries = ['ES', 'PT']
          })
        }
      })
    },

    handleKeyPress(event) {
      const key = event.key
      const value = event.target.value

      if (!/^\d$/.test(key)) {
        event.preventDefault()
      }

      if (value.length) {
        event.preventDefault()
      }
    },

    handleCountryChange({iso2}) {
      const nationalNumber = getExampleNumber(iso2, examples)?.formatNational()
      const formattedNationalNumber = nationalNumber.startsWith('0') ? nationalNumber.slice(0, -1) : nationalNumber
      const mask = formattedNationalNumber.replace(/\d/g, ' 9').trim()
      this.prefixCountrySelected = iso2

      if (!(this.initialPrefix && this.prefix === this.initialPrefix)) {
        this.resetSearchBar()
      }

      Inputmask({
        mask,
        placeholder: '_',
        clearIncomplete: false,
        showMaskOnHover: false,
        showMaskOnFocus: true,
      }).mask(this.$refs.phoneInput)
    },

    handlePhoneChange({target: {value}}) {
      this.isValidPhone = this.validatePhoneNumber(value)
      this.client = null
      this.isContactSearched = false
    },

    validatePhoneNumber(value) {
      const formattedValue = value.replace(/[\s_]/g, '')

      try {
        const phoneNumber = parsePhoneNumber(formattedValue, this.prefixCountrySelected)

        return phoneNumber?.isValid()
      } catch (error) {
        return false
      }
    },

    getFormattedPhoneNumber() {
      if (this.validatePhoneNumber(this.phoneNumber)) {
        const formattedPhone = this.phoneNumber.replace(/[\s_]/g, '')
        const countryCode = getCountryCallingCode(this.prefixCountrySelected)

        return `+${countryCode}${formattedPhone}`
      }

      return null
    },

    resetSearchBar() {
      this.phoneNumber = ''
      this.isValidPhone = false
      this.isContactSearched = false
      this.isDropdownOpen = false
      this.isPrefixSelectOpen = false
      this.client = null
    },

    async sendGAEvent(action, label) {
      await this.$gtm.trackEvent({category: 'Offers', action, label})
    },
  },
}
</script>
<style lang="scss">
$_height-select: 250px;
$_box-shadow-prefix: rgba(0, 0, 0, 0.16) 3px 3px 6px 0px;

#app button.v-btn.contact-search-bar__button {
  border-color: #9a9a9a;
  background-color: white;
  cursor: pointer;
  height: 38px;
  transition:
    background-color 0.2s ease-in,
    border-color 0.2s ease-in;

  div {
    padding: 0 12px;

    i {
      margin-right: 8px;
    }
  }

  &:hover {
    border-color: black;

    div {
      color: black;
    }
  }

  &--primary {
    background-color: #00803f;
    border-color: transparent;

    div {
      color: white;
    }

    &:hover {
      background-color: #006b37;
      border-color: transparent;

      div {
        color: white;
      }
    }

    &:disabled {
      background-color: #8c8c8ca6;
      cursor: not-allowed;

      div {
        color: #dedede;

        .v-icon {
          color: #dedede !important;
        }
      }
    }
  }

  &--view-history {
    height: 32px !important;
    transition: border-color 0.2s ease-in;
    color: rgba(0, 0, 0, 0.86) !important;
  }

  &--search-bar {
    height: 32px;
    min-width: 40px;
    margin: 4px 4px 4px 26px;
    border-radius: 6px !important;
    border-color: transparent;
    padding: 0 !important;
    background-color: #51555b;

    div {
      color: white;
      padding: 0 !important;

      i {
        margin-right: 1px !important;
      }
    }

    .v-progress-circular {
      width: 18px !important;
      height: 18px !important;
    }

    &:disabled {
      background-color: #aeb1b6;
      cursor: not-allowed;

      div {
        color: #e6e7e9;

        .v-icon {
          color: #e6e7e9 !important;
        }
      }
    }

    &:hover {
      background-color: #51555b;
      border-color: transparent;

      div {
        color: white;
      }
    }
  }
}

.contact-search-bar {
  $self: &;
  position: relative;
  font-family: 'ProximaNovaRegular';
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid transparent;
  background-color: #f2f2f2;
  border-radius: 4px;
  width: inherit;
  height: 40px;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.24);
  transition:
    background-color 0.2s ease-in,
    color 0.2s ease-in,
    box-shadow 0.2s ease-in;

  &:hover {
    box-shadow: 0 0 0 2px rgb(167, 3, 3);
  }

  &.no-hover:hover {
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.24);
  }

  &--focused {
    box-shadow: 0 0 0 1px #1976d2;
    background-color: white;
  }

  &:focus-within {
    box-shadow: 0 0 0 1px #1976d2;
    background-color: white;
  }

  &--readonly,
  &--readonly:hover,
  &--readonly &--focused,
  &--readonly:focus-within {
    user-select: none;
    background-color: #f2f2f2;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.24);

    &,
    input {
      color: var(--v-gray-lighten4);
    }

    .vti__dropdown {
      cursor: default;
    }
  }

  &__select-prefix {
    #{$self}__arrow-container {
      display: flex;
      gap: 6px;
      align-items: center;
      font-size: 16px;
    }

    .v-icon {
      &::before {
        height: 8.13px;
      }
    }
  }

  &__telephone-input {
    all: unset;
    width: 100%;
    padding: 10px 16px;
    font-family: 'ProximaNovaRegular';
    font-variant-numeric: tabular-nums;
    color: #000;
    text-align: center;

    &::placeholder {
      color: ccc;
    }
  }

  &__options {
    z-index: 2;
    position: absolute;
    background-color: white;
    top: 100%;
    left: -1px;
    width: inherit;
    margin-top: 2px;
    max-height: $_height-select;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 0 !important;
    border: 1px solid #ccc;
    border-radius: 0 0 8px 8px;
    box-shadow: $_box-shadow-prefix;
    width: calc(100% + 1px);

    li {
      list-style: none;
    }

    #{$self}__option {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 16px;
      padding: 16px 12px;
      cursor: pointer;
      transition: background-color 0.2s ease-in;

      &--offer {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;

        #{$self}__option-buttons {
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
      }

      &--creating {
        flex-direction: column;
        display: flex;
        align-items: flex-start;
        padding: 1rem;
        gap: 5px;

        #{$self}__option-buttons {
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
      }

      &:hover {
        background-color: #eee;

        .v-btn.contact-search-bar__button {
          border-color: black;
          color: black;
        }
      }

      #{$self}__option-info {
        overflow: hidden;
        text-align: left;
        // display: flex;

        &--creating {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          margin-bottom: 0.5rem;
        }

        #{$self}__info {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          text-align: left;
          margin-bottom: 0;
          line-height: 1.2;

          &:first-child {
            padding-bottom: 2px;
          }

          &--secondary {
            color: #898989;
            display: block;
          }
        }
      }
    }

    #{$self}__no-option {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      padding: 20px 16px 16px;
      transition: background-color 0.2s ease-in;

      &:hover {
        background-color: #eee;
      }

      p {
        margin-bottom: 0;
        margin-top: 8px;
        line-height: 1.1;
      }

      #{$self}__no-option-title {
        font-size: 20px;
        max-width: 280px;
      }

      #{$self}__no-option-subtitle {
        font-size: 14px;
        color: #898989;
      }

      #{$self}__no-option-buttons {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        margin-top: 20px;
        width: 100%;
        gap: 10px;
      }

      #{$self}__no-option-button {
        display: grid;
        grid-template-columns: repeat(1, 1fr);
        margin-top: 20px;
        width: 100%;
      }
    }
  }

  &__button--creating {
    width: 100%;
    height: 32px !important;
  }

  &__info--secondary {
    color: #898989;
  }

  .rotated-icon {
    transform: rotate(180deg);
    transition: transform 0.3s ease;
  }
}

// STYLES VEL SEL INPUT //

div.vue-tel-input {
  border: none;

  &:focus-within {
    box-shadow: none;
  }
}

input.vti__search_box {
  display: block;
  width: 93.5%;
  margin: 8px 12px;
  border-radius: 4px;
  padding: 8px 12px;
  height: 40px;
  position: relative;

  &:focus,
  &:hover {
    border-color: #1976d2;
  }

  &::placeholder {
    color: var(--placeholder-color, '#969696');
  }
}

.vti__input {
  display: none;
}

div.vti__flag-wrapper {
  display: none;
}

span.vti__flag {
  margin-left: 0;
  transform: scale(1.25);
  transform-origin: left center;
}

div.vti__dropdown {
  padding: 8px;
  min-width: 70px;
  position: unset;
  transition: background-color 0.2s ease-in;
  border-radius: 4px 0 0 4px;
}

ul.vti__dropdown-list.below {
  position: absolute;
  top: 40px;
  width: calc(100% + 2px) !important;
  max-height: $_height-select;
  padding-left: 0;
  border-radius: 0 0 8px 8px;
  box-shadow: $_box-shadow-prefix;

  li.vti__dropdown-item:first-of-type {
    border-top: 1px solid #cacaca;
  }
}

li.vti__dropdown-item {
  display: flex;
  align-self: center;
  gap: 4px;
  padding: 16px 12px;

  span {
    color: #898989;
  }

  span.vti__flag {
    transform-origin: left top;
  }
}
</style>
